import { Ellipsis, LayoutTemplate, Plus } from 'lucide-react';
import React, { FC, KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@column/ui';

import { StateTabs } from '~/app/Layout';
import { PageActions, PageHeaderBorder } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Empty } from '~/components/Table';
import { IconTransfers } from '~/elements';
import { useDeleteTransferTemplate, useListTransferTemplates } from '~/hooks/useTransferTemplates';
import { useNavigate } from '~/lib/navigation';
import { TransferTemplateResponse, TransferTemplateType } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { getFormattedDate } from '~/util';

import { annotateTemplateAccess } from './_utils/authz';

const PAGE_SIZE = 9;

const ALL_TEMPLATES_TAB = 'all';

const TEMPLATE_TYPES: Record<TransferTemplateType, { tab: string; label: string; className: string }> = {
  [TransferTemplateType.ACH]: { tab: 'ACH', label: 'ACH Transfer', className: 'text-blue-600 dark:text-blue-300' },
  [TransferTemplateType.Wire]: {
    tab: 'Wire',
    label: 'Wire Transfer',
    className: 'text-seafoam-700 dark:text-seafoam-300',
  },
  [TransferTemplateType.Book]: {
    tab: 'Book',
    label: 'Book Transfer',
    className: 'text-purple-600 dark:text-purple-300',
  },
};

// Mirrors CopyRow: full-bleed top borders, a medium secondary label, and a lighter value.
const TemplateStat: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <li className="flex items-center justify-between gap-4 border-t border-gray-150 p-4 text-sm leading-4 dark:border-white/10">
    <span className="font-medium whitespace-nowrap text-gray-600 dark:text-gray-500">{label}</span>
    <span className="truncate text-gray-450 dark:text-gray-550">{children}</span>
  </li>
);

export const PageTransferTemplatesOverview: FC = () => {
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const [hasMore, setHasMore] = useState<boolean>(false);
  const {
    createRequest: fetchTemplates,
    response: templates,
    isLoading,
    queryParams,
    setQueryParams,
  } = useListTransferTemplates({
    initialQueryParams: {
      page: 1,
      limit: PAGE_SIZE,
    },
    onSuccess: (response) => {
      if (!queryParams?.startingAfter && queryParams?.endingBefore) {
        setHasMore(true);
      } else {
        setHasMore(response.hasMore);
      }
    },
  });

  const { createRequest: deleteTemplate } = useDeleteTransferTemplate({
    onError: (error) => addDangerNotification({ content: error.message }),
    onSuccess: () => addSuccessNotification({ content: 'Template successfully deleted' }),
  });

  const navigate = useNavigate();

  const handleTemplateDelete = useCallback((template: TransferTemplateResponse) => {
    openAlert({
      headline: `Delete ${template.description}?`,
      content: "Are you sure you want to remove this transfer template and all it's related data?",
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        deleteTemplate({ transferTemplateId: template.transferTemplateId }).then(() => fetchTemplates());
      },
    });
  }, []);

  const handlePageNext = useCallback(() => {
    setQueryParams(
      {
        page: (Number(queryParams?.page ?? 1) + 1) as number,
        startingAfter: templates?.transferTemplates?.at(-1)?.transferTemplateId,
        endingBefore: undefined,
        limit: queryParams?.limit ?? PAGE_SIZE,
      },
      true
    );
  }, [queryParams, setQueryParams, templates]);

  const handlePagePrev = useCallback(() => {
    setQueryParams(
      {
        page: (Number(queryParams?.page ?? 1) - 1) as number,
        endingBefore: templates?.transferTemplates?.at(0)?.transferTemplateId,
        startingAfter: undefined,
        limit: queryParams?.limit ?? PAGE_SIZE,
      },
      true
    );
  }, [queryParams, setQueryParams, templates]);

  const activeTab = queryParams?.templateType ?? ALL_TEMPLATES_TAB;
  const templateTypes = templates?.templateTypes;
  const hasTabs = !!templateTypes?.length;

  const handleTabChange = useCallback(
    (value: string) => {
      setQueryParams(
        {
          page: 1,
          limit: PAGE_SIZE,
          templateType: value === ALL_TEMPLATES_TAB ? undefined : (value as TransferTemplateType),
        },
        true
      );
    },
    [setQueryParams]
  );

  // Fall back to all templates once the active type has none left (e.g. after deleting the last one).
  useEffect(() => {
    if (templateTypes && activeTab !== ALL_TEMPLATES_TAB && !templateTypes.includes(activeTab)) {
      handleTabChange(ALL_TEMPLATES_TAB);
    }
  }, [activeTab, templateTypes, handleTabChange]);

  const tabEntries = useMemo(
    () => [
      { label: 'All', value: ALL_TEMPLATES_TAB },
      ...Object.values(TransferTemplateType).map((type) => ({
        label: TEMPLATE_TYPES[type].tab,
        value: type,
        hide: !templateTypes?.includes(type),
      })),
    ],
    [templateTypes]
  );

  const annotatedTemplates = useMemo(() => {
    return templates?.transferTemplates?.map((template) => annotateTemplateAccess(template, isAuthorized));
  }, [isAuthorized, templates]);

  const createButton = (
    <Button size="sm" onClick={() => navigate(`${ROUTE.TRANSFERS}/templates/create`)}>
      <Plus />
      Create Template
    </Button>
  );

  const currentPage = Number(queryParams?.page ?? 1);

  return (
    <>
      <PageActions>{createButton}</PageActions>
      {/* The tab bar draws its own bottom line, so the header only needs one while it's hidden. */}
      <PageHeaderBorder mode={hasTabs ? false : 'onScroll'} />
      {hasTabs && <StateTabs value={activeTab} onValueChange={handleTabChange} entries={tabEntries} />}
      <Loading isLoading={!!isLoading}>
        {!!templates && templates.transferTemplates?.length > 0 ? (
          <div className="flex flex-col">
            <div
              className={cn(
                'grid grid-cols-1 gap-4 px-(--page-margin) pb-(--page-margin) min-[1024px]:grid-cols-2 min-[1260px]:grid-cols-3',
                hasTabs && 'pt-3'
              )}
            >
              {annotatedTemplates?.map((template) => {
                const isTemplateDisabled =
                  template.isPartialAccess ||
                  !isAuthorized({ permission: 'transferTemplates', permissionLevel: 'write' });
                const editPath = `${ROUTE.TRANSFERS}/templates/edit/${template.transferTemplateId}`;
                const templateType = template.templateType ? TEMPLATE_TYPES[template.templateType] : undefined;

                return (
                  <div
                    key={template.transferTemplateId}
                    data-cy="template-card"
                    role={isTemplateDisabled ? undefined : 'link'}
                    tabIndex={isTemplateDisabled ? undefined : 0}
                    aria-disabled={isTemplateDisabled || undefined}
                    onClick={() => !isTemplateDisabled && navigate(editPath)}
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                      if (!isTemplateDisabled && event.key === 'Enter' && event.target === event.currentTarget) {
                        navigate(editPath);
                      }
                    }}
                    className={cn(
                      'flex flex-col overflow-hidden rounded-lg border border-gray-150 outline-none dark:border-white/10',
                      'transition-[background-color,border-color,box-shadow] duration-200',
                      isTemplateDisabled
                        ? 'bg-gray-50 dark:bg-white/[0.04]'
                        : [
                            'cursor-pointer bg-gray-25 dark:bg-white/[0.02]',
                            'hover:border-transparent hover:bg-background hover:shadow-btn-secondary',
                            'dark:hover:bg-gray-800 dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.15)]',
                            'focus-visible:border-transparent focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:ring-[3px] focus-visible:ring-blue-500/15',
                          ]
                    )}
                  >
                    <div className="flex items-center gap-2 px-4 py-3">
                      <span
                        className={cn('flex shrink-0', templateType?.className ?? 'text-foreground/70')}
                        aria-hidden
                      >
                        <IconTransfers.Templates />
                      </span>
                      {template.isPartialAccess && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex shrink-0 rounded-md bg-red-500/15 p-[3px] text-destructive dark:bg-red-500/25 dark:text-red-400">
                                <Icon.AnimationLock className="[--icon-size-x:14px] [--icon-size-y:14px]" />
                                <span className="sr-only">Restricted</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">You do not have access to this template.</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <h3 className="m-0 min-w-0 flex-1 truncate p-0 text-sm leading-6 font-medium text-foreground">
                        {template.description}
                      </h3>
                      {templateType && (
                        <span
                          className={cn(
                            'shrink-0 text-xs leading-4 font-medium whitespace-nowrap',
                            templateType.className
                          )}
                        >
                          {templateType.label}
                        </span>
                      )}
                      {!isTemplateDisabled && (
                        // The menu is portaled, but React still bubbles its clicks through here to the card.
                        <div className="flex" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" size="icon-xs" variant="muted" aria-label="Options">
                                <Ellipsis />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" sideOffset={4} className="min-w-36">
                              <DropdownMenuItem onSelect={() => navigate(editPath)}>Edit</DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  navigate(`${ROUTE.TRANSFERS}/templates/review/${template.transferTemplateId}`)
                                }
                              >
                                Review
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem variant="destructive" onSelect={() => handleTemplateDelete(template)}>
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                    <ul className="m-0 mt-auto list-none p-0">
                      <TemplateStat label="Number of transfers">
                        {(template?.achTransfers ?? []).length +
                          (template?.wireTransfers ?? []).length +
                          (template?.bookTransfers ?? []).length}
                      </TemplateStat>
                      <TemplateStat label="Times used">{template?.usageCount ?? 0}</TemplateStat>
                      <TemplateStat label="Last used">
                        {template.lastUsedAt
                          ? getFormattedDate(new Date(template.lastUsedAt), 'month day hh:mm AM/PM')
                          : '-'}
                      </TemplateStat>
                    </ul>
                  </div>
                );
              })}
            </div>

            <DataTable.Pagination
              className="justify-end px-[var(--page-margin)] pb-4"
              pageIndex={currentPage - 1}
              pageSize={PAGE_SIZE}
              hasNext={hasMore}
              hasPrevious={currentPage > 1}
              onNext={handlePageNext}
              onPrevious={handlePagePrev}
            />
          </div>
        ) : (
          <Empty
            icon={<LayoutTemplate />}
            title="No templates found"
            description="Create your first transfer template."
            action={createButton}
          />
        )}
      </Loading>
    </>
  );
};
